import type { ComponentProps } from 'react';
import React from 'react';
import { type Href, Link } from 'expo-router';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import Animated, { useAnimatedStyle, useDerivedValue, withTiming } from 'react-native-reanimated';

import { BodyText, Icon, TagLabel, theme, usePressableState, useResponsive } from '@sg/garnish';

// ────────────────────────────────────────────────────────────────────────────────

export const AccountMenuLink = (props: Props) => {
  const { testID, label, iconName, active, href, tag, onPress } = props;

  const ref = React.useRef(null);
  const { currentBreakpoint, match } = useResponsive();
  const { isHovered } = usePressableState(ref);

  const shouldShowIcon = !currentBreakpoint.isSM;

  // ─── Animated Styles ─────────────────────────────────────────────────

  const hoverOverlayOpacitySV = useDerivedValue(() => {
    return withTiming(isHovered || active ? 1 : 0, { duration: theme.transitions.short });
  });

  const hoverOverlayAnimatedStyle = useAnimatedStyle(() => {
    return { opacity: hoverOverlayOpacitySV.value };
  });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Link
      href={href}
      testID={testID}
      accessibilityRole="link"
      accessibilityLabel={label}
      onPress={onPress}
      asChild
    >
      <TouchableOpacity ref={ref} style={styles.pressable} activeOpacity={0.7}>
        <Animated.View style={[styles.hoverOverlay, hoverOverlayAnimatedStyle]} />

        {shouldShowIcon ? (
          <View style={styles.iconWrapper}>
            <Icon name={iconName} />
          </View>
        ) : null}

        <BodyText size={match([2, 3])}>{label}</BodyText>

        {tag ? (
          <View style={styles.tagContainer}>
            <TagLabel size={3} palette={tag.palette}>
              {tag.title}
            </TagLabel>
          </View>
        ) : null}
      </TouchableOpacity>
    </Link>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  hoverOverlay: {
    position: 'absolute',
    top: 2,
    right: -10,
    bottom: 2,
    left: -10,
    borderRadius: theme.radius.large,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    opacity: 0,
  },
  pressable: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: theme.spacing['4'],
  },
  iconWrapper: {
    paddingRight: theme.spacing['4'],
  },
  tagContainer: {
    marginLeft: theme.spacing['2'],
  },
});

// ────────────────────────────────────────────────────────────────────────────────

type Props = {
  href: Href;
  label: string;
  iconName: ComponentProps<typeof Icon>['name'];
  testID?: string;
  active?: boolean;
  onPress?: () => void;
  tag?: {
    title: string;
    palette: ComponentProps<typeof TagLabel>['palette'];
  };
};
